import { buildTimelineLayout, generateDateTicks } from "./timelineLayout";

const LANE_SPACING = 44;
const LABEL_MARGIN = 72;
const PADDING_HEAD = 40;
const PADDING_TAIL = 80;
const NODE_RADIUS = 7;
const NATURAL_PX_PER_DAY = 0.85;
const LABEL_PAD = 140; // room for title labels to the right of nodes

/**
 * Build a self-contained SVG string for the active timeline at natural size.
 */
export function buildTimelineSvg({ events, orientation = "horizontal", title = "Linr" }) {
  const layout = buildTimelineLayout(events || []);
  const { treeNodes, connectors, branchLines, minDate, maxDate, laneCount } = layout;
  const isHorizontal = orientation === "horizontal";

  if (!treeNodes.length) {
    return emptySvg(title);
  }

  const totalDays = (() => {
    const diff = new Date(maxDate).getTime() - new Date(minDate).getTime();
    return Math.max(diff / (1000 * 60 * 60 * 24), 30);
  })();

  const timeAxisLength = Math.max(totalDays * NATURAL_PX_PER_DAY + PADDING_HEAD + PADDING_TAIL, 640);
  const laneAxisLength = Math.max((Math.max(laneCount, 1) - 1) * LANE_SPACING + LABEL_MARGIN + 160, 220);
  const timeRange = timeAxisLength - PADDING_HEAD - PADDING_TAIL;

  let svgWidth = isHorizontal ? timeAxisLength : laneAxisLength;
  let svgHeight = isHorizontal ? laneAxisLength : timeAxisLength;
  // Labels sit to the right of nodes
  svgWidth += LABEL_PAD;

  const dateToTimeCoord = (dateStr) => {
    const date = new Date(dateStr);
    const min = new Date(minDate);
    const max = new Date(maxDate);
    const range = max.getTime() - min.getTime();
    if (range === 0) return PADDING_HEAD + timeRange / 2;
    return PADDING_HEAD + ((date.getTime() - min.getTime()) / range) * timeRange;
  };

  const laneToCoord = (lane) => LABEL_MARGIN + lane * LANE_SPACING;

  const positions = {};
  for (const node of treeNodes) {
    const timeCoord = dateToTimeCoord(node.date);
    const laneCoord = laneToCoord(node.lane);
    positions[node.id] = {
      x: isHorizontal ? timeCoord : laneCoord,
      y: isHorizontal ? laneCoord : timeCoord,
    };
  }

  const ticks = generateDateTicks(minDate, maxDate, Math.max(timeRange, 100));
  const parts = [];

  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}" font-family="Inter, system-ui, sans-serif">`,
  );
  parts.push(`<rect width="100%" height="100%" fill="#f8f9fa"/>`);
  parts.push(`<title>${escapeXml(title)} — Linr</title>`);

  // Ticks / grid
  for (const tick of ticks) {
    const coord = dateToTimeCoord(tick.date);
    const isYear = tick.kind === "year";
    const stroke = isYear ? "#e2e4e8" : "#eef0f2";
    const label = isYear ? tick.date.slice(0, 4) : formatMonthShort(tick.date);
    const weight = isYear ? "700" : "400";
    const fill = isYear ? "#1a1d23" : "#6b7280";
    const dash = isYear ? "" : ` stroke-dasharray="4 4"`;

    if (isHorizontal) {
      parts.push(
        `<line x1="${coord}" y1="${LABEL_MARGIN - 16}" x2="${coord}" y2="${svgHeight}" stroke="${stroke}" stroke-width="${isYear ? 1 : 0.5}"${dash}/>`,
      );
      parts.push(
        `<text x="${coord}" y="${LABEL_MARGIN - 22}" text-anchor="middle" fill="${fill}" font-size="${isYear ? 13 : 11}" font-weight="${weight}">${escapeXml(label)}</text>`,
      );
    } else {
      parts.push(
        `<line x1="${LABEL_MARGIN - 10}" y1="${coord}" x2="${svgWidth}" y2="${coord}" stroke="${stroke}" stroke-width="${isYear ? 1 : 0.5}"${dash}/>`,
      );
      parts.push(
        `<text x="${LABEL_MARGIN - 16}" y="${coord + 4}" text-anchor="end" fill="${fill}" font-size="${isYear ? 13 : 11}" font-weight="${weight}">${escapeXml(label)}</text>`,
      );
    }
  }

  // Branch lines
  for (const bl of branchLines) {
    const timeStart = dateToTimeCoord(bl.startDate);
    const timeEnd = dateToTimeCoord(bl.endDate);
    const laneC = laneToCoord(bl.lane);
    const x1 = isHorizontal ? timeStart : laneC;
    const y1 = isHorizontal ? laneC : timeStart;
    const x2 = isHorizontal ? timeEnd : laneC;
    const y2 = isHorizontal ? laneC : timeEnd;
    parts.push(
      `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${bl.color}" stroke-width="3" stroke-linecap="round" opacity="0.45"/>`,
    );
  }

  // Range connectors
  for (const c of connectors.filter((x) => x.type === "range")) {
    const fromNode = treeNodes[c.fromIdx];
    const toNode = treeNodes[c.toIdx];
    const fromPos = positions[fromNode?.id];
    const toPos = positions[toNode?.id];
    if (!fromPos || !toPos) continue;
    const x1 = isHorizontal ? fromPos.x + NODE_RADIUS : fromPos.x;
    const y1 = isHorizontal ? fromPos.y : fromPos.y + NODE_RADIUS;
    const x2 = isHorizontal ? toPos.x - NODE_RADIUS : toPos.x;
    const y2 = isHorizontal ? toPos.y : toPos.y - NODE_RADIUS;
    parts.push(
      `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${c.color}" stroke-width="3" stroke-linecap="round" opacity="0.75"/>`,
    );
  }

  // Bifurcations
  for (const c of connectors.filter((x) => x.type === "bifurcation")) {
    const childNode = treeNodes.find((n) => n.eventId === c.eventId && n.type !== "range-end");
    const parentNode = treeNodes.find((n) => n.eventId === c.parentId && n.type !== "range-end");
    if (!childNode || !parentNode) continue;
    const childPos = positions[childNode.id];
    const parentPos = positions[parentNode.id];
    if (!childPos || !parentPos) continue;
    const d = bifurcationPath(parentPos, childPos, isHorizontal, NODE_RADIUS);
    parts.push(
      `<path d="${d}" stroke="${c.color}" stroke-width="2" stroke-dasharray="6 3" fill="none" opacity="0.6"/>`,
    );
  }

  // Nodes + labels
  for (const node of treeNodes) {
    const pos = positions[node.id];
    if (!pos) continue;
    const isEnd = node.type === "range-end";
    const r = isEnd ? 4 : NODE_RADIUS;
    parts.push(
      `<circle cx="${pos.x}" cy="${pos.y}" r="${r}" fill="${node.color}" stroke="#ffffff" stroke-width="${isEnd ? 1.5 : 2}"/>`,
    );
    parts.push(
      `<circle cx="${pos.x}" cy="${pos.y}" r="${r + 2}" fill="none" stroke="${node.color}" stroke-width="2" opacity="${isEnd ? 0.5 : 1}"/>`,
    );

    if (!isEnd && node.title) {
      const fontSize = parseInt(node.textSize, 10) || 14;
      const lx = pos.x + r + 10;
      const ly = pos.y + fontSize * 0.35;
      parts.push(
        `<rect x="${lx - 4}" y="${pos.y - fontSize * 0.7}" width="${estimateTextWidth(node.title, fontSize) + 8}" height="${fontSize + 8}" rx="4" fill="#ffffff" stroke="#eef0f2"/>`,
      );
      parts.push(
        `<text x="${lx}" y="${ly}" fill="#1a1d23" font-size="${fontSize}" font-weight="500">${escapeXml(node.title)}</text>`,
      );
    }
  }

  parts.push(`</svg>`);
  return parts.join("\n");
}

export function downloadSvg(svgString, filename) {
  const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function printSvg(svgString, title = "Linr") {
  const win = window.open("", "_blank");
  if (!win) {
    alert("Please allow popups to print.");
    return;
  }
  win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeXml(title)} — Linr</title>
  <style>
    @page { margin: 12mm; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { display: flex; justify-content: center; align-items: flex-start; padding: 16px; }
    svg { max-width: 100%; height: auto; }
  </style>
</head>
<body>${svgString}</body>
</html>`);
  win.document.close();
  // Give the browser a tick to layout before printing
  setTimeout(() => {
    win.focus();
    win.print();
  }, 250);
}

export function slugifyFilename(name) {
  const base = (name || "timeline")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `linr-${base || "timeline"}.svg`;
}

function emptySvg(title) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="240" viewBox="0 0 480 240" font-family="Inter, system-ui, sans-serif">
  <rect width="100%" height="100%" fill="#f8f9fa"/>
  <title>${escapeXml(title)} — Linr</title>
  <text x="240" y="120" text-anchor="middle" fill="#6b7280" font-size="14">No events to export</text>
</svg>`;
}

function bifurcationPath(parentPos, childPos, isHorizontal, nodeRadius) {
  if (isHorizontal) {
    const forkX = childPos.x;
    const forkY = parentPos.y;
    const endY = childPos.y;
    const midY = (forkY + endY) / 2;
    return `M ${forkX} ${forkY} C ${forkX} ${midY}, ${forkX} ${midY}, ${forkX} ${endY - Math.sign(endY - forkY || 1) * nodeRadius * 0.35}`;
  }
  const forkX = parentPos.x;
  const forkY = childPos.y;
  const endX = childPos.x;
  const midX = (forkX + endX) / 2;
  return `M ${forkX} ${forkY} C ${midX} ${forkY}, ${midX} ${forkY}, ${endX - Math.sign(endX - forkX || 1) * nodeRadius * 0.35} ${forkY}`;
}

function formatMonthShort(dateStr) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const m = parseInt(dateStr.slice(5, 7), 10) - 1;
  return months[m] || "";
}

function escapeXml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function estimateTextWidth(text, fontSize) {
  return Math.ceil(String(text).length * fontSize * 0.55);
}
